import { useCallback } from 'react';
import { useWebSocketContext } from '../contexts/WebSocketProvider';
import { CONNECTION_STATES } from '../constants/webSocketConstants';

/**
 * Basic WebSocket hook for accessing connection state and subscription methods
 * 
 * This hook provides:
 * - Connection state information
 * - Basic subscription methods
 * - Connection control methods
 * - Connection status helpers
 */
export const useWebSocket = () => {
  const context = useWebSocketContext();

  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }

  const {
    connectionState,
    isConnected,
    lastConnectedAt,
    lastError,
    connect,
    disconnect,
    reconnect,
    subscribe,
    send,
    getConnectionInfo,
    enabled,
    user
  } = context;

  /**
   * Check if WebSocket is in a specific state
   */
  const isState = useCallback((state) => {
    return connectionState === state;
  }, [connectionState]);

  /**
   * Check if WebSocket is connecting
   */
  const isConnecting = useCallback(() => {
    return connectionState === CONNECTION_STATES.CONNECTING;
  }, [connectionState]);

  /**
   * Check if WebSocket is reconnecting
   */
  const isReconnecting = useCallback(() => {
    return connectionState === CONNECTION_STATES.RECONNECTING;
  }, [connectionState]);

  /**
   * Check if WebSocket is in error state
   */
  const hasError = useCallback(() => {
    return connectionState === CONNECTION_STATES.ERROR;
  }, [connectionState]);

  /**
   * Check if WebSocket is disconnected
   */
  const isDisconnected = useCallback(() => {
    return connectionState === CONNECTION_STATES.DISCONNECTED;
  }, [connectionState]);

  /**
   * Get connection status for UI display
   */
  const getConnectionStatus = useCallback(() => {
    return {
      state: connectionState,
      isConnected,
      isConnecting: isConnecting(),
      isReconnecting: isReconnecting(),
      isDisconnected: isDisconnected(),
      hasError: hasError(),
      lastConnectedAt,
      lastError,
      enabled,
      user
    };
  }, [
    connectionState,
    isConnected,
    isConnecting,
    isReconnecting,
    isDisconnected,
    hasError,
    lastConnectedAt,
    lastError,
    enabled,
    user
  ]);

  /**
   * Subscribe with automatic cleanup
   * Returns subscription object that can be used to unsubscribe
   */
  const createSubscription = useCallback((destination, messageHandler, options = {}) => {
    return subscribe(destination, messageHandler, options);
  }, [subscribe]);

  return {
    // Connection state
    connectionState,
    isConnected,
    lastConnectedAt,
    lastError,

    // Connection state helpers
    isState,
    isConnecting,
    isReconnecting,
    isDisconnected,
    hasError,

    // Connection methods
    connect,
    disconnect,
    reconnect,

    // Messaging methods
    subscribe: createSubscription,
    send,

    // Utility methods
    getConnectionStatus,
    getConnectionInfo,

    // Configuration
    enabled,
    user
  };
};

export default useWebSocket;
